import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IconGripVertical, IconTrash } from "@tabler/icons-react";
import { Image } from "../../../../components/common/image";
import { Text } from "../../../../components/common/typography";
import type { FeaturedDealDto } from "../../../../api";

interface Props {
  deal: FeaturedDealDto;
  onRemove: (id: number) => void;
  disabled?: boolean;
}

export function SortableDealRow({ deal, onRemove, disabled }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: deal.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const product = deal.product;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 rounded-2xl border border-borderDefault bg-bgWhite p-3"
    >
      <button
        type="button"
        className="shrink-0 cursor-grab touch-none text-textMuted hover:text-textDark disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Drag to reorder"
        disabled={disabled}
        {...attributes}
        {...listeners}
      >
        <IconGripVertical size={20} />
      </button>

      <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-bgLight">
        <Image
          image={product?.image ?? undefined}
          alt={product?.displayName ?? product?.name ?? "Product"}
          size="w-full h-full"
          showPlaceholder
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <Text variant="bodyBold" tone="dark">
          {product?.displayName ?? product?.name ?? `Product #${deal.productTemplateId}`}
        </Text>
        {product?.defaultCode && (
          <Text variant="bodyXs" tone="muted">SKU {product.defaultCode}</Text>
        )}
      </div>

      <button
        type="button"
        className="shrink-0 rounded-lg p-2 text-textMuted hover:bg-bgLight hover:text-accentError disabled:cursor-not-allowed disabled:opacity-50"
        aria-label="Remove from Deal of the Week"
        disabled={disabled}
        onClick={() => onRemove(deal.id)}
      >
        <IconTrash size={18} />
      </button>
    </div>
  );
}
